// DocumentDrop.js
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/system';
import { IconButton, Button } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';

/* ───────────── styled helpers ─────────────────── */
const DropZoneContainer = styled('div')(({ isDragActive }) => ({
    width: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    background: isDragActive ? '#e0e0e0' : 'transparent',
    transition: 'background 0.3s ease-in-out',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
}));

const DragOverlayText = styled('div')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '10px 20px',
    borderRadius: '5px',
    display: 'none',
});

const FileList = styled('div')({
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const FileItem = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#f5f5f5',
    padding: '5px 10px',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '360px',
    marginBottom: '6px',
    gap: 10,
});

const FileActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
});

/* ───────────── component ───────────────────────── */
const DocumentDrop = ({
    autoFilling = false,
    documents = [],
    setDocuments = () => {},
    handleDocumentUpload = () => {},
    onPreviewDocument,
    onRemoveDocument,
    onClearDocuments,
    showDropzone = true,
    hideList = false,
    suppressLabelNames = false,
    containerStyle = {},
    fullWidth = false,
}) => {
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);

    

    const onDrop = useCallback(
        (acceptedFiles) => {
            setIsDragging(false);
            if (acceptedFiles.length > 0) {
                const updatedDocs = [...documents, ...acceptedFiles];
                setDocuments(updatedDocs);
                handleDocumentUpload({ target: { files: updatedDocs } });
            }
        },
        [documents, setDocuments, handleDocumentUpload]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDragEnter: () => setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
    });

    const clearSelectedFiles = () => {
        if (onClearDocuments) {
            onClearDocuments();
            return;
        }
        setDocuments([]);
        handleDocumentUpload({ target: { files: [] } });
    };

    const removeFile = (index) => {
        if (onRemoveDocument) {
            onRemoveDocument(index);
            return;
        }
        const updated = [...documents];
        updated.splice(index, 1);
        setDocuments(updated);
        handleDocumentUpload({ target: { files: updated } });
    };

    const handlePreview = (file, idx) => {
        if (onPreviewDocument) {
            onPreviewDocument(file, idx);
        }
    };
    if (!showDropzone && (!documents || documents.length === 0)) {
        return null;
    }
    return (
        <div className="newDocument-container-items" style={{ marginTop: '20px', ...containerStyle, ...(fullWidth && { width: '100%' }) }}>
            {/* Drag and drop area */}
            {showDropzone && (
                <div
                    className={`newDocument-file-upload-container ${autoFilling ? 'input-field_Container_required-asterisk' : ''}`}
                    {...getRootProps()}
                >
                    <DropZoneContainer isDragActive={isDragActive}>
                        <input {...getInputProps()} />

                        {isDragging && (
                            <DragOverlayText style={{ display: 'block' }}>
                                {t('DropFilesHere')}
                            </DragOverlayText>
                        )}

                        {!isDragging && (
                            <label className="newDocument-upload-label">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="upload-icon"
                                    viewBox="0 0 58 58"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M29 38.0625C30.001 38.0625 30.8125 37.251 30.8125 36.25V9.73298L34.874 14.4712C35.5253 15.2313 36.6696 15.3193 37.4296 14.6678C38.1896 14.0164 38.2776 12.8721 37.6261 12.1121L30.3761 3.65378C30.0319 3.25204 29.529 3.02084 29 3.02084C28.471 3.02084 27.9681 3.25204 27.624 3.65378L20.3739 12.1121C19.7224 12.8721 19.8104 14.0164 20.5705 14.6678C21.3305 15.3193 22.4747 15.2313 23.1262 14.4712L27.1875 9.73298V36.25C27.1875 37.251 27.999 38.0625 29 38.0625Z"
                                        fill="#F9AA2A"
                                    />
                                </svg>
                                <span className="upload-text">
                                    {!suppressLabelNames && documents.length > 0
                                        ? documents.map((d) => d.name).join(', ')
                                        : t('DragDropFilesOrClick')}
                                </span>
                            </label>
                        )}
                    </DropZoneContainer>
                </div>
            )}

            {/* Selected files list */}
            {!hideList && documents.length > 0 && (
                <FileList>
                    {documents.map((doc, idx) => (
                        <FileItem key={idx}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</span>
                            <FileActions>
                                {onPreviewDocument && (
                                    <IconButton onClick={() => handlePreview(doc, idx)} size="small">
                                        <VisibilityOutlinedIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton onClick={() => removeFile(idx)} size="small">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </FileActions>
                        </FileItem>
                    ))}
                    <Button
                        onClick={clearSelectedFiles}
                        color="secondary"
                        size="small"
                        startIcon={<ClearIcon />}
                    >
                        {t('ClearAll')}
                    </Button>
                </FileList>
            )}
        </div>
    );
};

export default DocumentDrop;
